import { useMemo, useState } from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { useMetricDirectory } from "@/metrics/registry"
import { Button } from "@coval/ui"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { MetricFilterModeToggle } from "@/components/filters/MetricFilterModeToggle"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import { useConversationsFilter } from "../../contexts/ConversationsFilterContext"
import { useAllMetrics } from "../../hooks/useAllMetrics"
import { useMetricStringValuesTransport } from "../../routes/useMetricStringValuesTransport"
import type { ConversationsFilterSectionProps } from "../../types/filters"
import { buildMetricFilterOptions } from "../../utils/metricFilterOptions"
import { MetricFilterPanel } from "./MetricFilterPanel"

const MetricsIcon = ENTITY_ICONS.metrics

export function MetricsFilterSection({
  isOpen,
  onToggle,
  filterEnabled = true,
  conversations,
}: ConversationsFilterSectionProps & {
  conversations: ConversationsTableResult[]
}) {
  const {
    state: { metricIds, metricValueFilters },
    setMetricIds,
    setMetricCondition,
  } = useConversationsFilter()
  const { data, isLoading, isError, refetch } = useAllMetrics({
    enabled: filterEnabled,
  })
  const { byManager, isLoading: directoryLoading } = useMetricDirectory()
  const metrics = useMemo(
    () => buildMetricFilterOptions(data ?? {}, conversations, byManager),
    [data, conversations, byManager],
  )
  const [panelMode, setPanelMode] = useState<"presence" | "value">("presence")
  const [selectedId, setSelectedId] = useState("")
  const selectedMetric = metrics.find((metric) => metric.id === selectedId)
  const values = useMetricStringValuesTransport(
    selectedMetric?.outputType === "string" && !selectedMetric.valueOptions
      ? selectedId
      : undefined,
  )
  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      trigger={
        <FilterTrigger
          label="Metrics"
          icon={<MetricsIcon className="size-4" />}
          isActive={metricIds.length + metricValueFilters.length > 0}
        />
      }
    >
      <MetricFilterModeToggle
        mode={panelMode}
        onModeChange={setPanelMode}
        presenceCount={metricIds.length}
        valueCount={metricValueFilters.length}
      />
      {panelMode === "presence" ? (
        <>
          {isError && (
            <div role="alert" className="p-3 text-sm text-coval-danger-text">
              Could not load metrics.{" "}
              <Button variant="link" onClick={() => void refetch()}>
                Retry metrics
              </Button>
            </div>
          )}
          <SearchableCheckboxList
            items={metrics.map((metric) => ({
              id: metric.id,
              label: metric.name,
              searchValue: metric.searchText,
            }))}
            selectedIds={metricIds}
            onSelectionChange={setMetricIds}
            isLoading={isLoading}
            isDeferred={!filterEnabled}
            isOpen={isOpen}
            placeholder="Search metrics..."
            emptyMessage="No metrics found"
            canAddItem={() => metricIds.length < 50}
            blockedAdditionMessage="A maximum of 50 metrics is supported."
          />
        </>
      ) : (
        <MetricFilterPanel
          metrics={metrics}
          metricIds={metricIds}
          filters={metricValueFilters}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onApply={setMetricCondition}
          loading={!filterEnabled || isLoading || directoryLoading}
          error={isError}
          onRetry={() => {
            void refetch()
          }}
          stringValues={values.data}
          valuesLoading={values.isLoading}
          valuesError={values.isError}
          onRetryValues={() => {
            void values.refetch()
          }}
        />
      )}
    </FilterPopover>
  )
}
